import { Button, Callout, FieldLabel, REASON_TOO_SHORT, ReasonField, TextArea, TextInput, errorText } from "@amber/ui";
import type { Duplicate } from "../../contracts/index";
import { DuplicateNotice } from "./DuplicateNotice";
import { Languages } from "./Languages";
import { ProposalView } from "./ProposalView";
import type { CreateFlow } from "./useCreateProfile";

function CompletingFields({ flow, completing }: { flow: CreateFlow; completing: Duplicate }) {
  return (
    <>
      <Callout className="mt-3" title={`Adds this identity to the draft ${completing.name}`}>
        Its sourced fields are filled in from Wikidata and Wikipedia, and source discovery drafts a first plan. Nothing
        is monitored yet.
      </Callout>
      <ReasonField value={flow.reason} onChange={flow.setReason} error={flow.reasonInvalid ? REASON_TOO_SHORT : null} />
    </>
  );
}

function DraftFields({ flow }: { flow: CreateFlow }) {
  return (
    <>
      <div className="mt-3">
        <FieldLabel htmlFor="profile-name" required>
          Display name
        </FieldLabel>
        <TextInput
          id="profile-name"
          value={flow.displayName}
          onChange={(e) => {
            flow.setDisplayName(e.target.value);
          }}
        />
      </div>
      <Languages value={flow.languages} onChange={flow.setLanguages} invalid={flow.languagesInvalid} />
      <div className="mt-3">
        <FieldLabel htmlFor="profile-rationale" optional="optional now, required for a plan">
          Rationale
        </FieldLabel>
        <TextArea
          id="profile-rationale"
          value={flow.rationale}
          placeholder="Why this person should be tracked"
          onChange={(e) => {
            flow.setRationale(e.target.value);
          }}
        />
      </div>
    </>
  );
}

export function ProposalStep({ flow }: { flow: CreateFlow }) {
  const { proposal, completing, different } = flow;
  if (proposal.isPending) return <p className="mt-3 text-md text-muted">Reading Wikipedia and linked editions…</p>;
  if (proposal.isError)
    return (
      <Callout
        tone="attention"
        title="Couldn't read this identity"
        actions={
          <Button size="sm" onClick={() => void proposal.refetch()}>
            Retry
          </Button>
        }
      >
        {errorText(proposal.error)}
      </Callout>
    );
  return (
    <>
      <div className="flex flex-col gap-2">
        {flow.duplicates
          .filter((d) => d.id !== completing?.id)
          .map((d) => (
            <DuplicateNotice
              key={d.id}
              duplicate={d}
              decided={different.includes(d.id)}
              onDifferent={() => {
                flow.setDifferent([...different, d.id]);
                if (completing?.id === d.id) flow.setCompleting(null);
              }}
              onComplete={
                d.can_complete
                  ? () => {
                      flow.setCompleting(d);
                    }
                  : null
              }
            />
          ))}
      </div>
      {proposal.data.editions
        .filter((e) => e.state === "error")
        .map((e) => (
          <Callout
            key={e.edition}
            tone="waiting"
            className="mt-2"
            title={`${e.edition}.wikipedia could not be read`}
            actions={
              <Button size="sm" onClick={() => void proposal.refetch()}>
                Retry
              </Button>
            }
          >
            {e.detail}
          </Callout>
        ))}
      <ProposalView proposal={proposal.data} accept={flow.accept} setAccept={flow.setAccept} />
      {completing ? <CompletingFields flow={flow} completing={completing} /> : <DraftFields flow={flow} />}
    </>
  );
}
